
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuthStore } from '../store';
import { Volume2, Music, CheckCircle, XCircle, ArrowRight, ArrowLeft, Settings, HelpCircle, Trophy } from 'lucide-react';

interface VocabItem {
    vocab_id: string;
    hanzi: string;
    pinyin: string;
    type: string;
    En_meaning: string;
    Id_meaning: string;
    version?: string; // '2' or '3'
    level?: number;
    audio?: string;
    picture?: string;
    s_audio?: string;
    Sentence?: string;
    Sentence_meaning?: string;
    // For local state
    p1Result?: 'correct' | 'wrong' | 'dont_know';
    p2PinyinResult?: 'correct' | 'wrong';
    p2MeaningResult?: 'correct' | 'wrong';
    finalOutcome?: 'correct' | 'wrong' | 'dont_know';
}

const PINYIN_STYLE: React.CSSProperties = { fontFamily: '"Times New Roman", Times, serif' };

const TestVocabReview: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token } = useAuthStore();
    const [loading, setLoading] = useState(true);
    const [vocabList, setVocabList] = useState<VocabItem[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [mode, setMode] = useState<'preview' | 'settings' | 'quiz_p1' | 'quiz_p2_pinyin' | 'quiz_p2_meaning' | 'result'>('preview');
    
    // Settings
    const [settings, setSettings] = useState({
        part1: true,
        part2: true,
        language: 'en' as 'en' | 'id'
    });

    // Quiz State
    const [options, setOptions] = useState<string[]>([]);
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
    const [showReviewContent, setShowReviewContent] = useState(false); // For wrong answer feedback

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const sAudioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        if (!token || !id) return;
        loadVocab();
    }, [token, id]);

    const loadVocab = async () => {
        try {
            setLoading(true);
            const res = await api.getWeakVocab(id!, token!); 
            
            if (res.success && res.vocab) {
                setVocabList(res.vocab);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    // Helper to construct media URL
    const getMediaUrl = (type: 'picture' | 'audio' | 'sentence_audio', item: VocabItem) => {
        // Use backend URL from environment
        const baseUrl = BACKEND_URL;
        
        let path = '';

        if (type === 'audio' && item.audio) {
            path = item.audio;
        } else if (type === 'picture' && item.picture) {
            path = item.picture;
        } else if (type === 'sentence_audio' && item.s_audio) {
            path = item.s_audio;
        }

        if (path) {
            if (path.startsWith('http')) return path;
            // Align with HSK3ListeningTest logic: simple concatenation, no double encoding
            return `${baseUrl}/media/${path.startsWith('/') ? path.slice(1) : path}`;
        }

        const v = item.version === '2' ? 'HSK2' : 'HSK3'; 
        const l = item.level || 1;
        
        let folder = '';
        let ext = '';
        let filename = '';
        
        if (type === 'picture') { 
            folder = 'Picture'; 
            ext = '.webp'; 
            filename = item.hanzi;
        } else if (type === 'audio' || type === 'sentence_audio') { 
            folder = 'Audio'; 
            ext = '.mp3';
            
            // Simple tone stripper
            const pinyin = item.pinyin || '';
            const toneMap: Record<string, string> = {
                'ā': 'a', 'á': 'a', 'ǎ': 'a', 'à': 'a',
                'ē': 'e', 'é': 'e', 'ě': 'e', 'è': 'e',
                'ī': 'i', 'í': 'i', 'ǐ': 'i', 'ì': 'i',
                'ō': 'o', 'ó': 'o', 'ǒ': 'o', 'ò': 'o',
                'ū': 'u', 'ú': 'u', 'ǔ': 'u', 'ù': 'u',
                'ü': 'v', 'ǘ': 'v', 'ǚ': 'v', 'ǜ': 'v',
                'ń': 'n', 'ň': 'n', 'ǹ': 'n',
                'Ā': 'A', 'Á': 'A', 'Ǎ': 'A', 'À': 'A',
                'Ē': 'E', 'É': 'E', 'Ě': 'E', 'È': 'E',
                'Ī': 'I', 'Í': 'I', 'Ǐ': 'I', 'Ì': 'I',
                'Ō': 'O', 'Ó': 'O', 'Ǒ': 'O', 'Ò': 'O',
                'Ū': 'U', 'Ú': 'U', 'Ǔ': 'U', 'Ù': 'U',
                'Ü': 'V', 'Ǘ': 'V', 'Ǚ': 'V', 'Ǜ': 'V'
            };
            
            let normalized = pinyin.split('').map(char => toneMap[char] || char).join('');
            // Remove non-alphanumeric chars (like spaces if multi-char word?)
            // Example: "wo men" -> "women"
            normalized = normalized.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
            
            filename = type === 'sentence_audio' ? `s-${normalized}` : normalized;
        }

        // Use encodeURIComponent for filename only
        return `${baseUrl}/media/Vocab ${v}/${folder}/HSK${l}/${encodeURIComponent(filename)}${ext}`;
    };

    // Auto-play audio when entering preview or quiz p1
    useEffect(() => {
        if (vocabList.length === 0) return;
        const item = vocabList[currentIndex];

        if (mode === 'preview') {
            playAudioSequence(item);
        } else if (mode === 'quiz_p1') {
            playAudio(getMediaUrl('audio', item));
        }
    }, [currentIndex, mode, vocabList]);

    const playAudio = (url: string): Promise<void> => {
        return new Promise((resolve) => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.onended = () => resolve();
            audio.play().catch(e => {
                console.warn('Audio play failed', e);
                resolve(); // Continue even if audio fails
            });
        });
    };

    const playAudioSequence = async (item: VocabItem) => {
        await playAudio(getMediaUrl('audio', item));
        // Wait a bit?
        await new Promise(r => setTimeout(r, 500));
        await playAudio(getMediaUrl('sentence_audio', item));
    };

    // --- Preview Phase ---
    const handlePreviewNext = () => {
        if (currentIndex < vocabList.length - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            setMode('settings');
        }
    };

    const handlePreviewBack = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        } else {
            navigate(-1);
        }
    };

    // --- Settings Phase ---
    const startReview = () => {
        if (!settings.part1 && !settings.part2) return;
        setCurrentIndex(0);
        // Initialize results tracking
        const initialized = vocabList.map(v => ({ ...v }));
        setVocabList(initialized);
        
        if (settings.part1) {
            setMode('quiz_p1');
            prepareQuizOptions(0, 'hanzi');
        } else {
            setMode('quiz_p2_pinyin');
            prepareQuizOptions(0, 'pinyin');
        }
    };

    // --- Quiz Logic ---
    const prepareQuizOptions = (idx: number, type: 'hanzi' | 'pinyin' | 'meaning') => {
        if (!vocabList[idx]) return;
        const correct = vocabList[idx];
        
        // Pick 3 distractors
        const distractors = vocabList.filter(v => v.vocab_id !== correct.vocab_id);
        const shuffled = distractors.sort(() => Math.random() - 0.5).slice(0, 3);
        const optionsPool = [correct, ...shuffled].sort(() => Math.random() - 0.5);
        
        if (type === 'hanzi') {
             setOptions(optionsPool.map(v => v.hanzi));
        } else if (type === 'pinyin') {
             setOptions(optionsPool.map(v => v.pinyin));
        } else {
             setOptions(optionsPool.map(v => settings.language === 'en' ? v.En_meaning : v.Id_meaning));
        }
        
        setSelectedOption(null);
        setIsCorrect(null);
        setShowReviewContent(false);
    };

    const handleQuizAnswer = (optionIdx: number) => { // -1 for Don't Know
        if (selectedOption !== null) return; // Already answered
        setSelectedOption(optionIdx);

        const currentVocab = vocabList[currentIndex];
        
        if (optionIdx === -1) {
            // Don't Know
            handleWrong(true);
            return;
        }

        const selectedText = options[optionIdx];
        let correctText = '';
        if (mode === 'quiz_p1') correctText = currentVocab.hanzi;
        else if (mode === 'quiz_p2_pinyin') correctText = currentVocab.pinyin;
        else correctText = settings.language === 'en' ? currentVocab.En_meaning : currentVocab.Id_meaning;

        if (selectedText === correctText) {
            handleCorrect();
        } else {
            handleWrong(false);
        }
    };

    const handleCorrect = () => {
        setIsCorrect(true);
        // Use BACKEND_URL from env for correct audio
        const audio = new Audio(`${BACKEND_URL}/media/hsk-media/Audio/correct-answer.mp3`); 
        audio.play().catch(() => {});

        // Update local state result
        const updated = [...vocabList];
        if (mode === 'quiz_p1') updated[currentIndex].p1Result = 'correct';
        else if (mode === 'quiz_p2_pinyin') updated[currentIndex].p2PinyinResult = 'correct';
        else updated[currentIndex].p2MeaningResult = 'correct';
        setVocabList(updated);

        setTimeout(() => {
            goNextStep();
        }, 300); // 0.3s delay
    };

    const handleWrong = (dontKnow: boolean) => {
        setIsCorrect(false);
        
        const updated = [...vocabList];
        const result = dontKnow ? 'dont_know' : 'wrong';
        
        if (mode === 'quiz_p1') updated[currentIndex].p1Result = result;
        else if (mode === 'quiz_p2_pinyin') updated[currentIndex].p2PinyinResult = result;
        else updated[currentIndex].p2MeaningResult = result;
        setVocabList(updated);

        // Show review content
        setShowReviewContent(true);
    };

    const goNextStep = () => {
        // Determine next step
        if (mode === 'quiz_p1') {
            // In Part 1, we go to next vocab until all done
            if (currentIndex < vocabList.length - 1) {
                setCurrentIndex(currentIndex + 1);
                prepareQuizOptions(currentIndex + 1, 'hanzi');
            } else {
                // Part 1 done. Check if Part 2 is enabled
                if (settings.part2) {
                    setCurrentIndex(0);
                    setMode('quiz_p2_pinyin');
                    prepareQuizOptions(0, 'pinyin');
                } else {
                    finishReview();
                }
            }
        } else if (mode === 'quiz_p2_pinyin') {
            // Even if wrong, we go to Meaning? 
            // User said: "Wait for Meaning answer... if Pinyin or Meaning wrong, show review page."
            // "Display pinyin... if correct/wrong... then display meaning".
            // So we always go to meaning for SAME vocab
            setMode('quiz_p2_meaning');
            prepareQuizOptions(currentIndex, 'meaning');
        } else if (mode === 'quiz_p2_meaning') {
            // Finished Part 2 for this vocab, go to next
            if (currentIndex < vocabList.length - 1) {
                setCurrentIndex(currentIndex + 1);
                setMode('quiz_p2_pinyin');
                prepareQuizOptions(currentIndex + 1, 'pinyin');
            } else {
                finishReview();
            }
        }
    };

    const finishReview = async () => {
        setMode('result');
        // Calculate final outcomes and submit
        const submissionItems = vocabList.map(v => {
            let outcome: 'correct' | 'wrong' | 'dont_know' = 'correct';
            
            // Logic:
            // "Part 1 wrong... even if Part 2 correct -> Wrong (-1)"
            // "Don't know -> -2"
            // "Only if P1 and P2 correct -> Correct (+1)"
            
            const p1 = v.p1Result;
            const p2p = v.p2PinyinResult;
            const p2m = v.p2MeaningResult;
            
            let isDontKnow = false;
            let isWrong = false;
            
            if (settings.part1) {
                if (p1 === 'dont_know') isDontKnow = true;
                else if (p1 === 'wrong') isWrong = true;
            }
            
            if (settings.part2) {
                if (p2p === 'dont_know' || p2m === 'dont_know') isDontKnow = true; // Wait, can p2 be dont know? No button for p2? User only mentioned "Don't know" for Part 1 (Hanzi).
                // "4 options... below is 'Don't know' option." - Context was Part 1.
                // For Part 2, "mechanism same as previous". So Part 2 also has Don't Know? 
                // Let's assume yes.
                
                // Correction: User said "Don't know" option under 4 options for Part 1.
                // For Part 2, "Same mechanism... just test content different". So yes.
                
                if (p2p === 'wrong' || p2m === 'wrong') isWrong = true;
            }

            if (isDontKnow) outcome = 'dont_know';
            else if (isWrong) outcome = 'wrong';
            else outcome = 'correct';

            return { vocab_id: v.vocab_id, outcome };
        });

        // Submit
        try {
            await api.updateVocabReview(submissionItems, token!, vocabList[0]?.version === '3' ? '3.0' : '2.0');
        } catch (e) {
            console.error('Failed to submit review', e);
        }
    };

    if (loading) return <div className="p-12 text-center">Loading vocab...</div>;
    if (vocabList.length === 0) return <div className="p-12 text-center">No weak vocabulary found for this test! Great job!</div>;

    const currentVocab = vocabList[currentIndex];

    // Render Logic
    if (mode === 'preview') {
        return (
            <div className="max-w-md mx-auto min-h-screen bg-gray-50 flex flex-col">
                {/* Progress */}
                <div className="p-4 text-center text-gray-500 font-bold">
                    {currentIndex + 1} of {vocabList.length}
                </div>

                {/* Card */}
                <div className="flex-1 px-6 flex flex-col justify-center items-center space-y-6">
                    <img 
                        src={getMediaUrl('picture', currentVocab)} 
                        alt={currentVocab.hanzi}
                        className="w-48 h-48 object-contain rounded-xl shadow-lg bg-white"
                        onError={(e) => (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200?text=No+Image'}
                    />
                    
                    <div className="text-center space-y-2">
                        <h1 className="text-6xl font-bold text-[#312E81]">{currentVocab.hanzi}</h1>
                        <p className="text-2xl text-gray-600" style={PINYIN_STYLE}>{currentVocab.pinyin}</p>
                    </div>

                    <div className="flex gap-4">
                        <button 
                            onClick={() => playAudio(getMediaUrl('audio', currentVocab))}
                            className="w-16 h-16 rounded-full bg-[#4F46E5] text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                        >
                            <Volume2 className="w-8 h-8" />
                        </button>
                        <button 
                            onClick={() => playAudio(getMediaUrl('sentence_audio', currentVocab))}
                            className="w-16 h-16 rounded-full bg-pink-500 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                        >
                            <Music className="w-8 h-8" />
                        </button>
                    </div>

                    <div className="text-center space-y-2 p-4 bg-white rounded-xl shadow-sm w-full">
                        <p className="text-lg font-bold text-gray-800">{currentVocab.En_meaning}</p>
                        <p className="text-sm text-gray-500">{currentVocab.Id_meaning}</p>
                        <span className="inline-block px-2 py-1 bg-gray-100 text-xs rounded text-gray-500">{currentVocab.type}</span>
                        {currentVocab.Sentence && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <p className="text-lg text-gray-800 mb-1">{currentVocab.Sentence}</p>
                                <p className="text-sm text-gray-500">{currentVocab.Sentence_meaning}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Nav */}
                <div className="p-6 flex justify-between">
                    <button onClick={handlePreviewBack} className="flex items-center text-gray-500 font-bold">
                        <ArrowLeft className="w-5 h-5 mr-1" /> Back
                    </button>
                    <button onClick={handlePreviewNext} className="flex items-center text-[#4F46E5] font-bold">
                        Next <ArrowRight className="w-5 h-5 ml-1" />
                    </button>
                </div>
            </div>
        );
    }

    if (mode === 'settings') {
        return (
            <div className="max-w-md mx-auto min-h-screen bg-white p-8 flex flex-col justify-center">
                <h1 className="text-3xl font-bold text-[#312E81] mb-8 flex items-center gap-2">
                    <Settings className="w-8 h-8" /> Review Settings
                </h1>

                <div className="space-y-6">
                    <div className="space-y-3">
                        <label className="font-bold text-gray-700">Parts to Review</label>
                        <div className="flex flex-col gap-3">
                            <label className="flex items-center gap-3 p-4 border rounded-xl bg-gray-50">
                                <input 
                                    type="checkbox" 
                                    checked={settings.part1} 
                                    onChange={e => setSettings(s => ({ ...s, part1: e.target.checked }))}
                                    className="w-5 h-5 text-[#4F46E5] rounded"
                                />
                                <span className="font-medium">Part 1 (Listening & Hanzi)</span>
                            </label>
                            <label className="flex items-center gap-3 p-4 border rounded-xl bg-gray-50">
                                <input 
                                    type="checkbox" 
                                    checked={settings.part2} 
                                    onChange={e => setSettings(s => ({ ...s, part2: e.target.checked }))}
                                    className="w-5 h-5 text-[#4F46E5] rounded"
                                />
                                <span className="font-medium">Part 2 (Pinyin & Meaning)</span>
                            </label>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <label className="font-bold text-gray-700">Meaning Language</label>
                        <div className="flex gap-3">
                            <button 
                                onClick={() => setSettings(s => ({ ...s, language: 'en' }))}
                                className={`flex-1 p-4 rounded-xl border font-bold transition-all ${settings.language === 'en' ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white text-gray-600'}`}
                            >
                                English
                            </button>
                            <button 
                                onClick={() => setSettings(s => ({ ...s, language: 'id' }))}
                                className={`flex-1 p-4 rounded-xl border font-bold transition-all ${settings.language === 'id' ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white text-gray-600'}`}
                            >
                                Indonesian
                            </button>
                        </div>
                    </div>

                    <button 
                        onClick={startReview}
                        disabled={!settings.part1 && !settings.part2}
                        className="w-full py-4 bg-[#4F46E5] text-white rounded-xl font-bold text-lg shadow-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    >
                        Start Review
                    </button>
                    
                    <button onClick={() => setMode('preview')} className="w-full py-3 text-gray-500 font-bold">
                        Back to Preview
                    </button>
                </div>
            </div>
        );
    }

    if (mode.startsWith('quiz')) {
        return (
            <div className="max-w-md mx-auto min-h-screen bg-gray-50 flex flex-col p-6">
                 {/* Header */}
                 <div className="flex justify-between items-center mb-8">
                    <span className="font-bold text-gray-400">{currentIndex + 1} / {vocabList.length}</span>
                    {mode === 'quiz_p1' && <Volume2 className="w-6 h-6 text-[#4F46E5] animate-pulse" />}
                 </div>

                 {/* Question */}
                 <div className="flex-1 flex flex-col items-center">
                    {mode === 'quiz_p1' && (
                        <div className="mb-8">
                            <button 
                                onClick={() => playAudio(getMediaUrl('audio', currentVocab))}
                                className="w-24 h-24 rounded-2xl bg-[#4F46E5] text-white flex items-center justify-center shadow-xl active:scale-95 transition-transform"
                            >
                                <Volume2 className="w-12 h-12" />
                            </button>
                        </div>
                    )}

                    {mode === 'quiz_p2_pinyin' && (
                        <div className="mb-8 text-center">
                            <p className="text-gray-500 mb-2">The pinyin of</p>
                            <h1 className="text-6xl font-bold text-[#312E81]">{currentVocab.hanzi}</h1>
                        </div>
                    )}
                    
                    {mode === 'quiz_p2_meaning' && (
                        <div className="mb-8 text-center">
                             <p className="text-gray-500 mb-2">The meaning of</p>
                             <h1 className="text-6xl font-bold text-[#312E81]">{currentVocab.hanzi}</h1>
                        </div>
                    )}

                    {/* Options */}
                    <div className="w-full space-y-3">
                        {options.map((opt, idx) => {
                            let stateClass = 'bg-white border-gray-200 text-gray-700';
                            if (selectedOption !== null) {
                                // Reveal logic
                                const isSelected = selectedOption === idx;
                                const isActuallyCorrect = 
                                    (mode === 'quiz_p1' && opt === currentVocab.hanzi) ||
                                    (mode === 'quiz_p2_pinyin' && opt === currentVocab.pinyin) ||
                                    (mode === 'quiz_p2_meaning' && opt === (settings.language === 'en' ? currentVocab.En_meaning : currentVocab.Id_meaning));
                                
                                if (isActuallyCorrect) stateClass = 'bg-green-100 border-green-500 text-green-800 ring-2 ring-green-500';
                                else if (isSelected) stateClass = 'bg-red-100 border-red-500 text-red-800 ring-2 ring-red-500';
                                else stateClass = 'bg-white border-gray-200 opacity-50';
                            }

                            return (
                                <button
                                    key={idx}
                                    onClick={() => handleQuizAnswer(idx)}
                                    disabled={selectedOption !== null}
                                    className={`w-full p-4 rounded-xl border-2 font-bold text-lg transition-all shadow-sm ${stateClass}`}
                                    style={mode === 'quiz_p2_pinyin' ? PINYIN_STYLE : undefined}
                                >
                                    {opt}
                                </button>
                            );
                        })}
                        
                        <button
                            onClick={() => handleQuizAnswer(-1)}
                            disabled={selectedOption !== null}
                            className={`w-full p-3 rounded-xl border border-transparent font-medium hover:bg-purple-100 transition-colors ${selectedOption !== null ? 'opacity-0' : 'text-purple-500 bg-purple-50'}`}
                        >
                            不知道 (Don't Know)
                        </button>
                    </div>

                    {/* Review Content (Shown if wrong) */}
                    {showReviewContent && (
                        <div className="w-full mt-6 p-4 bg-white rounded-xl shadow-lg border border-red-100 animate-in fade-in slide-in-from-bottom-4">
                            <div className="flex gap-4 mb-4">
                                <img src={getMediaUrl('picture', currentVocab)} className="w-20 h-20 object-contain rounded bg-gray-50" />
                                <div>
                                    <div className="text-2xl font-bold text-[#312E81]">{currentVocab.hanzi}</div>
                                    <div className="text-lg text-gray-600" style={PINYIN_STYLE}>{currentVocab.pinyin}</div>
                                    <div className="text-sm text-gray-500 mt-1">{currentVocab.En_meaning}</div>
                                </div>
                            </div>
                            <button onClick={goNextStep} className="w-full py-3 bg-[#312E81] text-white rounded-lg font-bold">
                                Next
                            </button>
                        </div>
                    )}
                 </div>
            </div>
        );
    }

    if (mode === 'result') {
        const correctCount = vocabList.filter(v => {
            // Re-calc logic for display
             // "Only if P1 and P2 correct -> Correct (+1)"
            const p1 = v.p1Result || (settings.part1 ? 'pending' : 'correct'); // If part skipped, treat as correct for logic? No, user said "If selected...". 
            // Actually, if part not selected, it doesn't affect correctness of the other part.
            // But for "Total Correct", we need ALL selected parts to be correct.
            
            let ok = true;
            if (settings.part1 && v.p1Result !== 'correct') ok = false;
            if (settings.part2 && (v.p2PinyinResult !== 'correct' || v.p2MeaningResult !== 'correct')) ok = false;
            return ok;
        }).length;

        return (
            <div className="max-w-md mx-auto min-h-screen bg-white p-8 flex flex-col justify-center items-center text-center">
                <div className="w-24 h-24 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mb-6">
                    <Trophy className="w-12 h-12" />
                </div>
                <h1 className="text-4xl font-bold text-[#312E81] mb-2">Review Complete!</h1>
                <p className="text-gray-500 mb-8">Great job reinforcing your vocabulary.</p>

                <div className="bg-gray-50 p-6 rounded-2xl w-full mb-8">
                    <div className="text-sm text-gray-500 font-bold uppercase tracking-wider mb-1">Score</div>
                    <div className="text-5xl font-bold text-[#4F46E5]">{correctCount} <span className="text-2xl text-gray-400">/ {vocabList.length}</span></div>
                </div>

                <button onClick={() => navigate(-1)} className="w-full py-4 bg-[#312E81] text-white rounded-xl font-bold shadow-lg">
                    Back to History
                </button>
            </div>
        );
    }

    return null;
};

export default TestVocabReview;
